"use client";

import type { RefObject } from "react";
import { useEffect, useMemo, useRef } from "react";
import type { ProductCardD2CClientProps } from "@/scott/ui/blocks/product-card-d2c/product-card-d2c.client";
import {
  type EcommerceItem,
  selectItemToDataLayer,
  viewItemListToDataLayer,
} from "@/utils/gtm.utils";

type UseGtmProductListingTrackingParams = {
  products: ProductCardD2CClientProps[];
  listName: string;
  listId?: string;
  containerRef?: RefObject<HTMLElement | null>;
};

const GRID_ITEM_SELECTOR = '[data-testid="product-listing-grid-item"]';
const GTM_PRODUCT_INDEX_ATTR = "data-gtm-product-index";
const VIEWPORT_DEBOUNCE_MS = 300;

function buildItemPayload(
  product: ProductCardD2CClientProps,
  index: number,
  listName: string,
  listId?: string
): EcommerceItem {
  const { gtm } = product;
  const cats = gtm?.categories ?? [];
  return {
    item_id: product.microcopies.productCode,
    item_name: gtm?.itemName ?? product.microcopies.name,
    item_variant_id: gtm?.variantCode,
    index,
    item_list_name: listName,
    ...(listId ? { item_list_id: listId } : {}),
    price: gtm?.priceValue,
    currency: gtm?.currency,
    item_brand: gtm?.brand,
    item_variant: gtm?.colorName,
    item_stock: gtm?.stockStatus,
    ...(cats[0] ? { item_category: cats[0].name } : {}),
    ...(cats[1] ? { item_category2: cats[1].name } : {}),
    ...(cats[2] ? { item_category3: cats[2].name } : {}),
    ...(cats[3] ? { item_category4: cats[3].name } : {}),
    ...(cats[4] ? { item_category5: cats[4].name } : {}),
  };
}

export function useGtmProductListingTracking({
  products,
  listName,
  listId,
  containerRef,
}: Readonly<UseGtmProductListingTrackingParams>) {
  const seenCodesRef = useRef<Set<string>>(new Set());
  const firstCodeRef = useRef<string>("");

  // Reset seen set when the product list is fully replaced (filter/sort change).
  // Load-more appends to the list so products[0] remains unchanged.
  const firstCode = products[0]?.microcopies.productCode ?? "";
  if (firstCodeRef.current !== firstCode) {
    seenCodesRef.current = new Set();
    firstCodeRef.current = firstCode;
  }

  useEffect(() => {
    const container = containerRef?.current;
    if (!(container && products.length)) {
      return;
    }

    const seenCodes = seenCodesRef.current;
    let pendingItems: EcommerceItem[] = [];
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    function flush() {
      if (!pendingItems.length) {
        return;
      }
      viewItemListToDataLayer([...pendingItems], listName, listId);
      pendingItems = [];
      debounceTimer = null;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            continue;
          }

          const el = entry.target as HTMLElement;
          const index = Number(el.getAttribute(GTM_PRODUCT_INDEX_ATTR));
          const product = products[index];
          if (!product) {
            continue;
          }

          const code = product.microcopies.productCode;
          observer.unobserve(el);

          if (seenCodes.has(code)) {
            continue;
          }
          seenCodes.add(code);

          pendingItems.push(buildItemPayload(product, index, listName, listId));

          if (debounceTimer !== null) {
            clearTimeout(debounceTimer);
          }
          debounceTimer = setTimeout(flush, VIEWPORT_DEBOUNCE_MS);
        }
      },
      { threshold: 0.5 }
    );

    const gridItems =
      container.querySelectorAll<HTMLElement>(GRID_ITEM_SELECTOR);
    gridItems.forEach((item, index) => {
      const product = products[index];
      if (!product || seenCodes.has(product.microcopies.productCode)) {
        return;
      }
      item.setAttribute(GTM_PRODUCT_INDEX_ATTR, String(index));
      observer.observe(item);
    });

    return () => {
      observer.disconnect();
      if (debounceTimer !== null) {
        clearTimeout(debounceTimer);
      }
      flush();
    };
  }, [products, listName, listId, containerRef]); // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(
    () =>
      products.map((product, index) => ({
        ...product,
        onClick: () => {
          selectItemToDataLayer(
            buildItemPayload(product, index, listName, listId),
            listName,
            listId
          );
          product.onClick?.();
        },
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products, listId, listName]
  );
}
